/**
 * Benefit-period choices for the LTC planning customisation panel.
 *
 * The panel used to offer the TOTAL period only (acceleration + continuation,
 * or "Lifetime"). That was enough while every Asset Care quote had a 2-year AOB.
 * With 3-year AOB quotes, "Lifetime" covers two different policies — a 2-year
 * and a 3-year acceleration, each with a lifetime continuation — and the panel
 * could only ever reach the first one it found.
 *
 * So a quote that states both periods is identified by the pair, and its label
 * says so: "5 years (3 Years AOB + 2 Years COB)", "Lifetime (3 Years AOB)".
 * A quote that gives only a total keeps the choice it always had.
 */
import type { Quote } from '@client/model';
import {
  LIFETIME_PERIOD,
  benefitPeriodToString,
  formatBenefitPeriod,
  getBenefitPeriodFromQuote,
  parseDurationYears,
  type BenefitPeriod,
} from '@/utils/benefitPeriod';

export interface BenefitPeriodOption {
  key: string;
  label: string;
}

interface SortableOption extends BenefitPeriodOption {
  total: BenefitPeriod;
  aobYears: number;
}

type PeriodQuote = Pick<Quote, 'aobDuration' | 'cobDuration' | 'ltcBenefitDurationMonths'>;

function describe(quote: PeriodQuote): SortableOption {
  const total = getBenefitPeriodFromQuote(quote as Quote);
  const aob = quote.aobDuration?.trim();
  const cob = quote.cobDuration?.trim();
  const aobYears = aob ? parseDurationYears(aob) : 0;

  if (!aob || !cob || typeof aobYears !== 'number' || aobYears <= 0) {
    return { key: benefitPeriodToString(total), label: formatBenefitPeriod(total), total, aobYears: 0 };
  }

  const cobYears = parseDurationYears(cob);
  const key = `${aobYears}+${benefitPeriodToString(cobYears)}`;
  if (cobYears === LIFETIME_PERIOD) {
    return { key, label: `Lifetime (${aob} AOB)`, total, aobYears };
  }
  if (cobYears === 0) {
    return { key, label: `${formatBenefitPeriod(aobYears)} (AOB only)`, total, aobYears };
  }
  return { key, label: `${formatBenefitPeriod(total)} (${aob} AOB + ${cob} COB)`, total, aobYears };
}

export function getBenefitPeriodOption(quote: PeriodQuote): BenefitPeriodOption {
  const { key, label } = describe(quote);
  return { key, label };
}

/** One option per distinct period, shortest first, lifetime last. */
export function buildBenefitPeriodOptions(quotes: PeriodQuote[]): BenefitPeriodOption[] {
  const byKey = new Map<string, SortableOption>();
  quotes.forEach(quote => {
    const option = describe(quote);
    if (option.total === LIFETIME_PERIOD || option.total > 0) byKey.set(option.key, option);
  });

  const rank = (total: BenefitPeriod) => (total === LIFETIME_PERIOD ? Infinity : total);
  return Array.from(byKey.values())
    .sort((first, second) => {
      if (rank(first.total) !== rank(second.total)) return rank(first.total) < rank(second.total) ? -1 : 1;
      return first.aobYears - second.aobYears;
    })
    .map(({ key, label }) => ({ key, label }));
}
